import * as React from "react";
import {
  CircleMarker,
  MapContainer,
  Pane,
  Polygon,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import { Link } from "react-router";

import type {
  LatLngTuple,
  LeafletEventHandlerFnMap,
  PathOptions,
} from "leaflet";
import "leaflet/dist/leaflet.css";

import { cn } from "@/lib/utils";
import {
  UNPARENTED_COLOUR,
  useAreaColours,
} from "@/pages/areas/lib/part-colours";
import type { Area, LatLng } from "@/types/area";

export interface MapVenue {
  id: string;
  name: string;
  lat: number;
  lng: number;
  /** False for a live venue that no area holds yet. */
  hasArea: boolean;
}

export const MAX_VENUE_PINS = 1000;

/**
 * The map's own panes, all between Leaflet's overlay pane (400) and its shadow pane (500): part
 * outlines at the bottom, areas above them, venue pins on top, whatever order the data loads in.
 * Each pane is its own SVG whose root lets clicks through, so a click reaches the topmost
 * interactive shape under it. All stay under the marker pane (600), the tooltip pane (650) and
 * the popup pane (700).
 *
 * A react-leaflet `Popup` inside a `Pane` inherits that pane, which drew the area popup inside the
 * areas pane, under the shapes, with its Edit link unclickable. Every popup here names
 * `POPUP_PANE` explicitly.
 */
export const MAP_PANES = {
  parts: { name: "area-parts", zIndex: 410 },
  areas: { name: "area-areas", zIndex: 420 },
  pins: { name: "area-pins", zIndex: 430 },
} as const;

/** Leaflet's own popup pane (z-index 700, above every pane here and the tooltip pane's 650). */
export const POPUP_PANE = "popupPane";

export interface AreaMapProps {
  areas: Area[];
  venues: MapVenue[];
  /** How many venues exist, when `venues` holds only the first pages of them. */
  totalVenues?: number;
  /** Drawn heavier than the rest. */
  highlightId?: string;
  onAreaClick?: (id: string) => void;
  /** `"all"` fits every shape; an id fits that area. Default `"all"`. */
  fitTo?: "all" | string;
  /** Bump to fit again to the same `fitTo` (a second pick of the same tree row). */
  fitRequest?: number;
  /**
   * The colours from `useAreaColours(areas)`, when the caller also draws a legend: one
   * computation for both. Computed here when absent (the edit-page preview).
   */
  colours?: Map<string, string>;
  className?: string;
}

const MIN_SHAPE_POINTS = 3;

const toPositions = (shape: LatLng[]): LatLngTuple[] =>
  shape.map((p) => [p.latitude, p.longitude]);

/** A curator-created area has no shape until the nightly writes one; it cannot be drawn. */
const drawable = (a: Area): boolean =>
  (a.shape?.length ?? 0) >= MIN_SHAPE_POINTS;

/**
 * Fits the map to `points` when `fitKey` changes, not when the arrays are merely rebuilt: a
 * refetch or an unrelated re-render must not throw away the curator's pan and zoom.
 */
function FitBounds({
  fitKey,
  points,
}: {
  fitKey: string;
  points: LatLngTuple[];
}) {
  const map = useMap();
  const latest = React.useRef(points);
  latest.current = points;
  React.useEffect(() => {
    if (latest.current.length)
      map.fitBounds(latest.current, { padding: [24, 24], maxZoom: 16 });
  }, [map, fitKey]);
  return null;
}

/**
 * Leaflet measures its container once, at creation. A container that mounts hidden or at another
 * size (a tab, a flex layout settling, a window resize) leaves grey tiles and wrong bounds until
 * `invalidateSize`, so it runs on mount and whenever the container's box changes.
 */
function MapSizeWatcher() {
  const map = useMap();
  React.useEffect(() => {
    map.invalidateSize();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);
  return null;
}

const NO_AREAS: Area[] = [];

export const AreaMap = ({
  areas,
  venues,
  totalVenues,
  highlightId,
  onAreaClick,
  fitTo = "all",
  fitRequest = 0,
  colours: givenColours,
  className,
}: AreaMapProps) => {
  const drawn = React.useMemo(
    () => (areas.length ? areas.filter(drawable) : NO_AREAS),
    [areas],
  );
  const ownColours = useAreaColours(givenColours ? NO_AREAS : areas);
  const colours = givenColours ?? ownColours;
  const partName = React.useMemo(
    () =>
      new Map(
        areas.filter((a) => a.kind === "part").map((p) => [p.id, p.name]),
      ),
    [areas],
  );
  const positions = React.useMemo(
    () => new Map(drawn.map((a) => [a.id, toPositions(a.shape)])),
    [drawn],
  );

  const target = React.useMemo(
    () => (fitTo === "all" ? drawn : drawn.filter((a) => a.id === fitTo)),
    [drawn, fitTo],
  );
  const fitPoints = React.useMemo(
    () => target.flatMap((a) => positions.get(a.id) ?? []),
    [target, positions],
  );
  // Ids and shape hashes: the same city's areas refetched give the same key, a new city a new one.
  const fitKey = `${fitTo}|${fitRequest}|${target.map((a) => `${a.id}:${a.shapeHash ?? a.shape.length}`).join(",")}`;

  // Kept in a ref so a new callback identity does not rebind every shape's click handler.
  const onClick = React.useRef(onAreaClick);
  onClick.current = onAreaClick;

  // react-leaflet calls `setStyle`/`setLatLngs` and rebinds handlers whenever the identity of
  // `pathOptions`/`positions`/`eventHandlers` changes. These objects are built once per set of
  // areas, so a highlight change hands new props only to the shapes it restyles.
  const styles = React.useMemo(
    () =>
      new Map(
        drawn.map((a) => {
          const colour = colours.get(a.id) ?? UNPARENTED_COLOUR;
          if (a.kind === "part") {
            const outline = (weight: number): PathOptions => ({
              color: colour,
              weight,
              fill: false,
              interactive: false,
            });
            return [a.id, { normal: outline(3), highlighted: outline(5) }];
          }
          const fill = (weight: number, fillOpacity: number): PathOptions => ({
            color: colour,
            fillColor: colour,
            weight,
            fillOpacity,
            interactive: true,
            ...(colour === UNPARENTED_COLOUR ? { dashArray: "6 6" } : {}),
          });
          return [
            a.id,
            { normal: fill(1.5, 0.35), highlighted: fill(4, 0.55) },
          ];
        }),
      ),
    [drawn, colours],
  );
  const handlers = React.useMemo(
    () =>
      new Map(
        drawn.map((a) => [
          a.id,
          { click: () => onClick.current?.(a.id) } as LeafletEventHandlerFnMap,
        ]),
      ),
    [drawn],
  );
  const styleOf = (id: string): PathOptions => {
    const s = styles.get(id)!;
    return id === highlightId ? s.highlighted : s.normal;
  };

  const partLayers = drawn
    .filter((a) => a.kind === "part")
    .map((a) => (
      <Polygon
        key={a.id}
        pane={MAP_PANES.parts.name}
        positions={positions.get(a.id)!}
        pathOptions={styleOf(a.id)}
      />
    ));

  const areaLayers = drawn
    .filter((a) => a.kind !== "part")
    .map((a) => {
      const part = a.parentId ? partName.get(a.parentId) : undefined;
      return (
        <Polygon
          key={a.id}
          pane={MAP_PANES.areas.name}
          positions={positions.get(a.id)!}
          pathOptions={styleOf(a.id)}
          eventHandlers={handlers.get(a.id)}
        >
          <Popup pane={POPUP_PANE}>
            <div className="space-y-1">
              <div className="font-medium">{a.name}</div>
              {part && <div>Part: {part}</div>}
              <div>{a.venueCount} venues</div>
              <Link
                to={`/areas/edit/${encodeURIComponent(a.id)}`}
                className="underline"
              >
                Edit
              </Link>
            </div>
          </Popup>
        </Polygon>
      );
    });

  const pins = React.useMemo(() => venues.slice(0, MAX_VENUE_PINS), [venues]);
  const pinLayers = React.useMemo(
    () =>
      pins.map((v) => {
        const colour = v.hasArea ? "#1f2937" : "#dc2626";
        return (
          <CircleMarker
            key={v.id}
            pane={MAP_PANES.pins.name}
            center={[v.lat, v.lng]}
            radius={4}
            pathOptions={{
              color: colour,
              fillColor: colour,
              fillOpacity: 0.8,
              weight: 1,
            }}
          >
            <Popup pane={POPUP_PANE}>
              <div>{v.name}</div>
              <div>{v.hasArea ? "Has an area" : "No area"}</div>
            </Popup>
          </CircleMarker>
        );
      }),
    [pins],
  );
  const venueTotal = Math.max(totalVenues ?? 0, venues.length);

  return (
    // `isolate`: Leaflet's panes and controls use z-index 400-1000; a stacking context of its own
    // keeps all of that under the page's popovers, selects and dialogs (z-50). `isolation:
    // isolate` alone creates the context, with no z-index of its own to compete with the page.
    <div
      data-testid="area-map"
      className={cn("relative isolate h-[600px] w-full", className)}
    >
      <MapContainer
        center={[20, 0]}
        zoom={2}
        scrollWheelZoom
        className="h-full w-full rounded-md"
      >
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <MapSizeWatcher />
        <FitBounds fitKey={fitKey} points={fitPoints} />
        <Pane
          name={MAP_PANES.parts.name}
          style={{ zIndex: MAP_PANES.parts.zIndex }}
        >
          {partLayers}
        </Pane>
        <Pane
          name={MAP_PANES.areas.name}
          style={{ zIndex: MAP_PANES.areas.zIndex }}
        >
          {areaLayers}
        </Pane>
        <Pane
          name={MAP_PANES.pins.name}
          style={{ zIndex: MAP_PANES.pins.zIndex }}
        >
          {pinLayers}
        </Pane>
      </MapContainer>
      {venueTotal > pins.length && (
        <div className="absolute bottom-2 left-2 z-[1000] rounded bg-background/90 px-2 py-1 text-xs shadow">
          Showing {pins.length} of {venueTotal} venues
        </div>
      )}
    </div>
  );
};
